import React, { useEffect, useRef, useState } from 'react';
import Button from '../../../components/ui/Button';
import { api } from '../../../lib/api';
import SubscriptionRequiredModal from '../../../components/ui/SubscriptionRequiredModal';
import { isSubscriptionError } from '../../../lib/subscriptionGate';

interface Props {
  homeworkId: number;
  onSubmitted?: () => void;
}

type MySubmission = {
  id: number;
  updated_at?: string;
  created_at?: string;
  files?: Array<{ file_url: string; original_name: string }>;
};

const HomeworkSubmissionButton: React.FC<Props> = ({ homeworkId, onSubmitted }) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [submission, setSubmission] = useState<MySubmission | null>(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const loadStatus = async () => {
    if (!homeworkId) return;
    setStatusLoading(true);
    try {
      const resp = await api.get(`/api/teme/${encodeURIComponent(String(homeworkId))}/rezolvare/me`);
      setSubmission((resp.data as MySubmission | null) || null);
    } catch {
      setSubmission(null);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    void loadStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeworkId]);

  const submit = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const form = new FormData();
      Array.from(files).forEach((f) => form.append('files', f));
      await api.post(`/api/teme/${encodeURIComponent(String(homeworkId))}/rezolvare`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await loadStatus();
      onSubmitted?.();
    } catch (err: any) {
      if (isSubscriptionError(err)) {
        setUpgradeOpen(true);
        return;
      }
      window.alert(err?.response?.data?.detail || 'Nu s-a putut incarca tema rezolvata.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const submittedAt = submission?.updated_at || submission?.created_at || null;
  const submittedAtLabel = submittedAt ? new Date(submittedAt).toLocaleString('ro-RO') : '—';

  return (
    <div className="flex flex-col items-end gap-1">
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          void submit(e.target.files);
        }}
      />
      <Button
        variant="outline"
        size="sm"
        iconName="Upload"
        iconPosition="left"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
      >
        {uploading ? 'Se incarca...' : 'Incarca tema rezolvata'}
      </Button>
      {statusLoading ? (
        <div className="text-xs text-muted-foreground">Verific...</div>
      ) : submission ? (
        <div className="text-xs text-success text-right">
          <div>Tema incarcata</div>
          <div className="text-muted-foreground">Incarcata la: {submittedAtLabel}</div>
        </div>
      ) : (
        <div className="text-xs text-muted-foreground">Tema neincarcata</div>
      )}

      <SubscriptionRequiredModal open={upgradeOpen} feature="homework_submit" onClose={() => setUpgradeOpen(false)} />
    </div>
  );
};

export default HomeworkSubmissionButton;
